import { getMobileNavigation } from "@/lib/mobile-navigation";
import type { TFunction } from "i18next";

export type TourRole = "hr" | "employee";
type Side = "top" | "right" | "bottom" | "left";

/**
 * A single tour stop. `route` (when set) is navigated to before the step is
 * shown — this is what lets the tour go *inside* a module. `element` is a CSS
 * selector to spotlight; omit it for a centred modal. The controller waits for
 * the element to appear after navigation before advancing.
 */
export interface TourStep {
  route?: string;
  element?: string;
  title: string;
  description: string;
  side?: Side;
  align?: "start" | "center" | "end";
  /** Sidebar nav titleKey gating this step; skipped if the module is disabled. */
  moduleKey?: string;
}

/**
 * Resolve the tour variant for a user's main role. Admin / line-manager /
 * c-level fall back to the HR flow (they share the Administration menu); plain
 * users get the employee flow. Returns the role-tour key used both to pick the
 * step list and to persist completion (`tour_done_<role>`).
 */
export function tourRoleForMainRole(mainRole?: string): TourRole {
  return mainRole === "hr" || mainRole === "admin" ? "hr" : "employee";
}

function s(
  t: TFunction,
  role: TourRole,
  key: string,
  opts: { route?: string; element?: string; side?: Side; align?: TourStep["align"]; moduleKey?: string } = {},
): TourStep {
  return {
    route: opts.route,
    element: opts.element,
    side: opts.side,
    align: opts.align ?? "start",
    moduleKey: opts.moduleKey,
    title: t(`tour.${role}.${key}.title`),
    description: t(`tour.${role}.${key}.description`),
  };
}

/**
 * Module overview step: navigate into the page and spotlight its content.
 * `moduleKey` is the sidebar nav titleKey gating the module — the controller
 * skips the step when that module is disabled for the tenant.
 */
function moduleStep(t: TFunction, key: string, route: string, moduleKey: string): TourStep {
  return s(t, "hr", key, { route, element: '[data-tour="page-main"]', side: "top", align: "center", moduleKey });
}

function employeeSteps(t: TFunction): TourStep[] {
  const r: TourRole = "employee";
  return [
    // The welcome step is rendered as a React overlay (TourWelcome), not a driver step.
    s(t, r, "headerNotifications", { element: '[data-tour="header-notifications"]', side: "bottom" }),
    s(t, r, "timeOff", { element: '[data-tour="widget-time_off"]', side: "left" }),
    s(t, r, "myTasks", { element: '[data-tour="header-tasks"]', side: "bottom", moduleKey: "myTasks" }),
    s(t, r, "documents", { element: '[data-tour="widget-documents"]', side: "left" }),
    // Go inside the key employee pages (skipped if the module is disabled)
    s(t, r, "myProfile", { route: "/my-profile", element: '[data-tour="page-main"]', side: "top", align: "center", moduleKey: "myProfile" }),
    s(t, r, "leave", { route: "/leave", element: '[data-tour="page-main"]', side: "top", align: "center", moduleKey: "leaveManagement" }),
    s(t, r, "myDocuments", { route: "/my-documents", element: '[data-tour="page-main"]', side: "top", align: "center", moduleKey: "myDocuments" }),
    s(t, r, "surveys", { route: "/my-surveys", element: '[data-tour="page-main"]', side: "top", align: "center", moduleKey: "mySurveys" }),
    s(t, r, "settings", { route: "/settings", element: '[data-tour="page-main"]', side: "top", align: "center" }),
  ];
}

function hrSteps(t: TFunction): TourStep[] {
  const r: TourRole = "hr";
  return [
    // The welcome step is rendered as a React overlay (TourWelcome), not a driver step.
    // HR dashboard landmarks (HR/admin land on /hr-dashboard)
    s(t, r, "overview", { element: '[data-tour="hrd-overview"]', side: "bottom" }),
    s(t, r, "dashboardToggle", { element: '[data-tour="dashboard-toggle"]', side: "bottom" }),
    s(t, r, "quickActions", { element: '[data-tour="hrd-quick-actions"]', side: "bottom" }),
    s(t, r, "approvals", { element: '[data-tour="hrd-approvals"]', side: "top" }),
    // Header controls (present on every page)
    s(t, r, "headerTasks", { element: '[data-tour="header-tasks"]', side: "bottom" }),
    s(t, r, "headerNotifications", { element: '[data-tour="header-notifications"]', side: "bottom" }),
    // Menus
    s(t, r, "mainMenu", { element: '[data-tour="section-main"]' }),
    s(t, r, "adminMenu", { element: '[data-tour="section-administration"]' }),
    // Inside each Administration module (skipped if the module is disabled)
    moduleStep(t, "people", "/employees", "peopleDirectory"),
    moduleStep(t, "organization", "/organization", "organization"),
    moduleStep(t, "teamLeave", "/team-leave", "teamLeave"),
    moduleStep(t, "recruitment", "/recruitment", "recruitment"),
    moduleStep(t, "hrDocuments", "/documents", "hrDocuments"),
    moduleStep(t, "payroll", "/payroll", "payroll"),
    moduleStep(t, "onboarding", "/onboarding", "onboarding"),
    moduleStep(t, "workflows", "/workflows", "workflows"),
    moduleStep(t, "expenses", "/expenses", "expenses"),
    moduleStep(t, "equipment", "/equipment", "equipmentManagement"),
    moduleStep(t, "medical", "/medical-checkups", "medicalCheckups"),
    moduleStep(t, "surveys", "/surveys", "surveys"),
    moduleStep(t, "cases", "/hr-cases", "hrOpenCases"),
    moduleStep(t, "certifications", "/certifications", "certifications"),
    moduleStep(t, "events", "/corporate-events", "corporateEvents"),
    moduleStep(t, "gdpr", "/gdpr-retention", "gdprRetention"),
    moduleStep(t, "analytics", "/hr-analytics", "hrAnalytics"),
    // Wrap up
    s(t, r, "settings", { route: "/settings", element: '[data-tour="page-main"]', side: "top", align: "center" }),
  ];
}

export function getTourSteps(role: TourRole, t: TFunction): TourStep[] {
  return role === "hr" ? hrSteps(t) : employeeSteps(t);
}

/**
 * Mobile tour — a single-screen walkthrough of the bottom navigation (the
 * welcome/finish steps are React overlays). No route navigation needed since
 * the bottom nav is always present.
 */
export function getMobileTourSteps(t: TFunction, canAccess: (key: string) => boolean = () => true): TourStep[] {
  const m = (key: string, sel: string): TourStep => ({
    element: sel,
    title: t(`tour.mobile.${key}.title`),
    description: t(`tour.mobile.${key}.description`),
    side: "top",
    align: "center",
  });
  return getMobileNavigation(canAccess).map(item => m(item.key, `[data-tour-m="nav-${item.key}"]`));
}
